import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Pause, SkipForward, SkipBack, RotateCcw, 
  Shuffle, X, ArrowLeft, CheckCircle, Code2 
} from 'lucide-react';
import { getLessonById } from '@/data/lessonsData';
import { getExecutionSteps } from '@/utils/executionEngine';
import { LESSON } from '@/constants/testIds';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import ArrayVisualization from '@/components/visualizers/ArrayVisualization';
import TwoPointerVisualization from '@/components/visualizers/TwoPointerVisualization';
import SearchVisualization from '@/components/visualizers/SearchVisualization';
import SortVisualization from '@/components/visualizers/SortVisualization';
import RecursionVisualization from '@/components/visualizers/RecursionVisualization';
import BacktrackingVisualization from '@/components/visualizers/BacktrackingVisualization';
import DPVisualization from '@/components/visualizers/DPVisualization';
import GraphVisualization from '@/components/visualizers/GraphVisualization';
import TreeVisualization from '@/components/visualizers/TreeVisualization';
import StringMatchVisualization from '@/components/visualizers/StringMatchVisualization';
import { logActivity } from '@/utils/api';
import { useCourseLanguage } from '@/data/languages';
import { getLessonCode } from '@/data/localized';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

const Lesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const lesson = getLessonById(lessonId);

  const [arrayInput, setArrayInput] = useState('');
  const [targetInput, setTargetInput] = useState('');
  const [currentArray, setCurrentArray] = useState([]);
  const [target, setTarget] = useState(null);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState([1]);
  const [showCompletion, setShowCompletion] = useState(false);
  const [lang] = useCourseLanguage();

  useEffect(() => {
    if (!lesson) {
      navigate('/dashboard');
    }
  }, [lesson, navigate]);

  useEffect(() => {
    let interval;
    if (isPlaying && currentStep < steps.length - 1) {
      const delays = [2000, 1000, 500, 250, 100];
      const delay = delays[speed[0]];
      
      interval = setInterval(() => {
        setCurrentStep(prev => {
          if (prev >= steps.length - 1) {
            setIsPlaying(false);
            setShowCompletion(true);
            return prev;
          }
          return prev + 1;
        });
      }, delay);
    } else if (currentStep >= steps.length - 1) {
      setIsPlaying(false);
      if (steps.length > 0) {
        setShowCompletion(true);
      }
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentStep, steps.length, speed]);

  // Log lesson completion (idempotent per learner + lesson)
  useEffect(() => {
    if (showCompletion && steps.length > 0) {
      logActivity('lesson_completed', lessonId);
    }
  }, [showCompletion, lessonId, steps.length]);

  if (!lesson) return null;

  const handleLoad = () => {
    try {
      // Custom string input (LCS, Knapsack) - pass raw string
      if (lesson.customStringInput) {
        if (!arrayInput.trim()) {
          alert('Please enter valid input');
          return;
        }
        // Set a display array for visualization if applicable
        setCurrentArray([]);
        const executionSteps = getExecutionSteps(lessonId, arrayInput.trim(), null);
        setSteps(executionSteps);
        setCurrentStep(-1);
        setIsPlaying(false);
        setShowCompletion(false);
        return;
      }

      const arr = arrayInput.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
      if (arr.length === 0) {
        alert('Please enter a valid array');
        return;
      }
      setCurrentArray(arr);
      
      let tgt = null;
      if (lesson.requiresTarget && targetInput.trim()) {
        tgt = parseInt(targetInput.trim());
        if (isNaN(tgt)) {
          alert('Please enter a valid target number');
          return;
        }
        setTarget(tgt);
      }
      
      const executionSteps = getExecutionSteps(lessonId, arr, tgt);
      setSteps(executionSteps);
      setCurrentStep(-1);
      setIsPlaying(false);
      setShowCompletion(false);
    } catch (error) {
      alert('Invalid input. Please check your input format.');
    }
  };

  const handlePlay = () => {
    if (currentStep === -1 && steps.length > 0) {
      setCurrentStep(0);
    }
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      setIsPlaying(false);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setIsPlaying(false);
    }
  };

  const handleRestart = () => {
    setCurrentStep(-1);
    setIsPlaying(false);
    setShowCompletion(false);
  };

  const handleReset = () => {
    setArrayInput('');
    setTargetInput('');
    setCurrentArray([]);
    setTarget(null);
    setSteps([]);
    setCurrentStep(-1);
    setIsPlaying(false);
    setShowCompletion(false);
  };

  const handleRandomExample = () => {
    const example = lesson.examples[Math.floor(Math.random() * lesson.examples.length)];
    // For customStringInput lessons, input is already a string
    if (lesson.customStringInput) {
      setArrayInput(example.input);
    } else {
      setArrayInput(example.input.join(', '));
    }
    if (lesson.requiresTarget && example.target !== null) {
      setTargetInput(example.target.toString());
    }
  };

  const currentStepData = currentStep >= 0 ? steps[currentStep] : null;

  const langCode = getLessonCode(lesson, lang);
  const activeLine = currentStepData ? langCode.toLine(currentStepData.line) : null;

  // Render code (selected language) with line highlighting
  const renderCode = () => {
    const lines = langCode.code.split('\n');
    return lines.map((line, index) => {
      const lineNumber = index + 1;
      const isActive = activeLine === lineNumber;
      
      return (
        <div
          key={index}
          className={`flex gap-4 px-4 py-1 ${isActive ? 'bg-white/10 border-l-2 border-[#FFB800] -ml-4 pl-3' : ''}`}
        >
          <span className="text-[#71717A] text-xs font-code select-none w-6 text-right flex-shrink-0">
            {lineNumber}
          </span>
          <pre className="text-sm font-code text-white flex-1 overflow-x-auto">
            <code>{line}</code>
          </pre>
        </div>
      );
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'Medium':
        return 'bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20';
      case 'Hard':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      default:
        return 'bg-white/10 text-white border-white/20';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      <div 
        data-testid={LESSON.container}
        className="pt-20 pb-4 h-screen max-h-screen overflow-hidden"
      >
        <div className="h-full px-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* LEFT PANEL - Problem Statement */}
          <div 
            data-testid={LESSON.problemPanel}
            className="col-span-12 lg:col-span-3 flex flex-col gap-4 overflow-y-auto h-full pb-4"
          >
            {/* Back Button */}
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full border-white/10 text-white hover:bg-white/5 font-body"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>

            {/* Problem Info */}
            <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <Badge className={`${getDifficultyColor(lesson.difficulty)} border font-code text-xs`}>
                  {lesson.difficulty}
                </Badge>
                <span className="text-[#71717A] text-xs font-code">{lesson.module}</span>
              </div>
              
              <h1 className="font-heading text-2xl font-bold text-white">
                {lesson.title}
              </h1>
              
              <div className="space-y-3">
                <div>
                  <h3 className="text-[#FFB800] text-sm font-heading font-semibold mb-2">Problem</h3>
                  <p className="text-[#A1A1AA] text-sm font-body leading-relaxed">
                    {lesson.problemStatement}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-[#FFB800] text-sm font-heading font-semibold mb-2">Explanation</h3>
                  <p className="text-[#A1A1AA] text-sm font-body leading-relaxed">
                    {lesson.explanation}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-[#FFB800] text-sm font-heading font-semibold mb-2">Real-Life Analogy</h3>
                  <p className="text-[#A1A1AA] text-sm font-body leading-relaxed">
                    {lesson.realLifeAnalogy}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-[#FFB800] text-sm font-heading font-semibold mb-2">Examples</h3>
                  <div className="space-y-2">
                    {lesson.examples.slice(0, 2).map((ex, i) => (
                      <div key={i} className="bg-[#1A1A20] border border-white/10 rounded p-3 text-xs font-code">
                        <div className="text-[#71717A]">Input: {typeof ex.input === 'string' ? ex.input : `[${ex.input.join(', ')}]`}</div>
                        {ex.target !== null && ex.target !== undefined && (
                          <div className="text-[#71717A]">Target: {ex.target}</div>
                        )}
                        <div className="text-[#FFB800]">Output: {Array.isArray(ex.output) ? `[${ex.output.join(', ')}]` : ex.output}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Input Controls */}
            <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-6 space-y-4">
              <h3 className="text-white font-heading font-semibold flex items-center gap-2">
                <Code2 className="w-4 h-4 text-[#FFB800]" />
                Input Controls
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="text-[#A1A1AA] text-xs font-code mb-2 block">
                    {lesson.inputLabel || (lesson.singleNumberInput ? 'Number (N)' : 'Array (comma separated)')}
                  </label>
                  <Input
                    data-testid={LESSON.arrayInput}
                    type="text"
                    placeholder={lesson.inputPlaceholder || (lesson.singleNumberInput ? 'e.g., 5' : 'e.g., 2, 7, 11, 15')}
                    value={arrayInput}
                    onChange={(e) => setArrayInput(e.target.value)}
                    className="bg-[#1A1A20] border-white/20 text-white placeholder:text-[#71717A] font-code text-sm"
                  />
                </div>
                
                {lesson.requiresTarget && (
                  <div>
                    <label className="text-[#A1A1AA] text-xs font-code mb-2 block">
                      Target
                    </label>
                    <Input
                      data-testid={LESSON.targetInput}
                      type="text"
                      placeholder="e.g., 9"
                      value={targetInput}
                      onChange={(e) => setTargetInput(e.target.value)}
                      className="bg-[#1A1A20] border-white/20 text-white placeholder:text-[#71717A] font-code text-sm"
                    />
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button
                    data-testid={LESSON.loadButton}
                    onClick={handleLoad}
                    className="flex-1 bg-[#FFB800] hover:bg-[#FFC520] text-black font-code font-semibold text-sm"
                  >
                    Load
                  </Button>
                  <Button
                    data-testid={LESSON.randomButton}
                    onClick={handleRandomExample}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 font-code text-sm"
                  >
                    <Shuffle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* CENTER PANEL - Java Code */}
          <div 
            data-testid={LESSON.codePanel}
            className="col-span-12 lg:col-span-4 bg-[#0A0A0A] rounded-xl border border-white/10 flex flex-col overflow-hidden h-full relative"
          >
            <div className="bg-[#0F0F12] px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-[#FFB800]" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <span className="text-[#71717A] text-xs font-code" data-testid="code-file-name">{langCode.file}</span>
            </div>
            <div className="px-4 py-2 border-b border-white/10 bg-[#0C0C0F] flex justify-center">
              <LanguageSwitcher testIdPrefix="lesson-lang" />
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {renderCode()}
            </div>
          </div>

          {/* RIGHT PANEL - Visualization */}
          <div 
            data-testid={LESSON.visualizationPanel}
            className="col-span-12 lg:col-span-5 bg-[#0F0F12] rounded-xl border border-white/10 flex flex-col overflow-hidden relative p-6 h-full"
          >
            <h3 className="text-white font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#FFB800] animate-pulse" />
              Live Visualization
            </h3>

            <div className="flex-1 overflow-y-auto space-y-6">
              {currentArray.length === 0 && steps.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 rounded-full bg-[#FFB800]/10 flex items-center justify-center mx-auto">
                      <Code2 className="w-8 h-8 text-[#FFB800]" />
                    </div>
                    <p className="text-[#A1A1AA] font-body">Load input to start visualization</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Visualization Area */}
                  <div className="min-h-[200px] flex items-center justify-center">
                    {lesson.visualizationType === 'array' && (
                      <ArrayVisualization 
                        array={currentArray} 
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                    {lesson.visualizationType === 'twoPointer' && (
                      <TwoPointerVisualization 
                        array={currentArray}
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                    {lesson.visualizationType === 'search' && (
                      <SearchVisualization 
                        array={currentArray}
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                    {lesson.visualizationType === 'sort' && (
                      <SortVisualization 
                        array={currentArray}
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                    {lesson.visualizationType === 'recursion' && (
                      <RecursionVisualization 
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                    {lesson.visualizationType === 'backtracking' && (
                      <BacktrackingVisualization 
                        array={currentArray}
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                    {lesson.visualizationType === 'dp' && (
                      <DPVisualization 
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                    {lesson.visualizationType === 'graph' && (
                      <GraphVisualization 
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                    {lesson.visualizationType === 'tree' && (
                      <TreeVisualization 
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                    {lesson.visualizationType === 'string-match' && (
                      <StringMatchVisualization 
                        step={currentStepData}
                        lessonId={lessonId}
                      />
                    )}
                  </div>

                  {/* Execution Info */}
                  {currentStepData && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-[#1A1A20] border border-white/10 rounded-lg p-4 space-y-3"
                    >
                      <div>
                        <div className="text-[#71717A] text-xs font-code mb-1">Current Step</div>
                        <div className="text-white font-body text-sm">{currentStepData.description}</div>
                      </div>
                      
                      {currentStepData.variables && (
                        <div>
                          <div className="text-[#71717A] text-xs font-code mb-2">Variables</div>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(currentStepData.variables).map(([key, value]) => (
                              <div key={key} className="bg-[#0F0F12] border border-white/10 rounded px-2 py-1">
                                <span className="text-[#FFB800] text-xs font-code">{key}</span>
                                <span className="text-white text-xs font-code ml-1">= {JSON.stringify(value)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-xs font-code">
                        <span className="text-[#71717A]">Step {currentStep + 1} of {steps.length}</span>
                        <span className="text-[#007AFF]">Line {activeLine}</span>
                      </div>
                    </motion.div>
                  )}
                </>
              )}
            </div>

            {/* Control Bar */}
            {steps.length > 0 && (
              <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
                <div className="flex items-center gap-2">
                  <Button
                    data-testid={LESSON.prevButton}
                    onClick={handlePrev}
                    disabled={currentStep <= 0}
                    size="icon"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 disabled:opacity-30"
                  >
                    <SkipBack className="w-4 h-4" />
                  </Button>
                  
                  {!isPlaying ? (
                    <Button
                      data-testid={LESSON.playButton}
                      onClick={handlePlay}
                      disabled={steps.length === 0}
                      className="flex-1 bg-[#FFB800] hover:bg-[#FFC520] text-black font-code font-semibold"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Play
                    </Button>
                  ) : (
                    <Button
                      data-testid={LESSON.pauseButton}
                      onClick={handlePause}
                      className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] text-white font-code font-semibold"
                    >
                      <Pause className="w-4 h-4 mr-2" />
                      Pause
                    </Button>
                  )}
                  
                  <Button
                    data-testid={LESSON.nextButton}
                    onClick={handleNext}
                    disabled={currentStep >= steps.length - 1}
                    size="icon"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 disabled:opacity-30"
                  >
                    <SkipForward className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    data-testid={LESSON.restartButton}
                    onClick={handleRestart}
                    size="icon"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    data-testid={LESSON.resetButton}
                    onClick={handleReset}
                    size="icon"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Speed Control */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[#71717A] text-xs font-code">Speed</span>
                    <span className="text-[#FFB800] text-xs font-code font-bold">
                      {['0.5x', '1x', '2x', '3x', 'Instant'][speed[0]]}
                    </span>
                  </div>
                  <Slider
                    data-testid={LESSON.speedControl}
                    value={speed}
                    onValueChange={setSpeed}
                    min={0}
                    max={4}
                    step={1}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Completion Modal */}
      <AnimatePresence>
        {showCompletion && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCompletion(false)}
          >
            <motion.div
              data-testid={LESSON.completionScreen}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0F0F12] border border-[#FFB800]/30 rounded-2xl p-8 max-w-md w-full space-y-6 shadow-[0_0_50px_rgba(255,184,0,0.2)]"
            >
              <div className="text-center space-y-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(34,197,94,0.4)]">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                
                <h2 className="font-heading text-3xl font-bold text-white">
                  Execution Complete!
                </h2>
                
                <div className="space-y-2">
                  <div className="bg-[#1A1A20] border border-white/10 rounded-lg p-3">
                    <div className="text-[#71717A] text-xs font-code mb-1">Total Steps</div>
                    <div className="text-[#FFB800] text-2xl font-code font-bold">{steps.length}</div>
                  </div>
                  
                  {currentStepData?.result !== undefined && (
                    <div className="bg-[#1A1A20] border border-white/10 rounded-lg p-3">
                      <div className="text-[#71717A] text-xs font-code mb-1">Final Result</div>
                      <div className="text-[#007AFF] text-2xl font-code font-bold">
                        {Array.isArray(currentStepData.result) 
                          ? `[${currentStepData.result.join(', ')}]` 
                          : currentStepData.result}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleRestart}
                  className="flex-1 bg-[#FFB800] hover:bg-[#FFC520] text-black font-heading font-semibold"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restart
                </Button>
                <Button
                  data-testid={LESSON.backToDashboard}
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/5 font-heading font-semibold"
                >
                  Dashboard
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Lesson;
